import asyncSeries from 'async/series';
import React from 'react';
import {
  Routes,
  Route,
  useOutlet,
  useLocation,
  useNavigate,
  Navigate,
} from 'react-router-dom';

import ModalDialog from './components/dialogs/modal_dialog';

import AccountScreen from './account_screen';
import BucketEditScreen from './bucket_edit_screen';
import CreateFolderScreen from './create_folder_screen';
import DeviceScreen from './device_screen';
import DeviceListScreen from './device_list_screen';
import HomeScreen from './home_screen';
import LoadingScreen from './loading_screen';
import LoginScreen from './login_screen';
import LogoutScreen from './logout_screen';
import NotFoundScreen from './not_found_screen';
import OrgListScreen from './org_list_screen';
import OrgScreen from './org_screen';
import UserEditScreen from './user_edit_screen';
import UserListScreen from './user_list_screen';
import VolumeScreen from './volume_screen';
import VolumeAuditScreen from './volume_audit_screen';
import VolumeListScreen from './volume_list_screen';
import VolumeSettingScreen from './volume_setting_screen';
import VolumeMetricScreen from './volume_metric_screen';
import VolumeUploadScreen from './volume_upload_screen';

import Stores from './stores';
import Managers from './managers';

import UserStore from './stores/user_store';

import Analytics from './tools/analytics';
import Api from './tools/api';
import util from './tools/util';

import './css/colors.css';

const _startup = util.herdOnce((done: any) => {
  util.log('ares-web: startup:', util.getVersion());
  util.setDebugLogger(Analytics.log);
  Analytics.init();
  asyncSeries([Api.init, Stores.init, Managers.init], (err) => {
    if (err) {
      util.errorLog('ares-web: startup err:', err);
    }
    util.log('ares-web: startup done');
    Analytics.loadFinish();
    done();
  });
});

export default function App() {
  const [is_loading, setLoading] = React.useState(true);
  const start_url = useLocation()?.pathname;
  const navigate = useNavigate();
  React.useEffect(
    () =>
      _startup(() => {
        if (!UserStore.isReady()) {
          navigate('/loading', { state: { from: start_url ?? '/' } });
        }
        setLoading(false);
      }),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  let content: any = null;
  if (is_loading) {
    content = <LoadingScreen noNav={true} />;
  } else {
    content = (
      <>
        <Routes>
          <Route path='/loading' element={<LoadingScreen forever={true} />} />
          <Route path='/login' element={<LoginScreen />} />
          <Route path='/logout' element={<LogoutScreen />} />
          <Route element={<ProtectedLayout />}>
            <Route path='/' element={<HomeScreen />} />
            <Route path='/device' element={<DeviceListScreen />} />
            <Route path='/volume' element={<VolumeListScreen />} />
            <Route path='/volume/:volume_id/' element={<VolumeScreen />} />
            <Route path='/volume/:volume_id/*' element={<VolumeScreen />} />
            <Route path='/upload/:volume_id' element={<VolumeUploadScreen />} />
            <Route
              path='/upload/:volume_id/*'
              element={<VolumeUploadScreen />}
            />
            <Route path='/audit/:volume_id/' element={<VolumeAuditScreen />} />
            <Route
              path='/metric/:volume_id/'
              element={<VolumeMetricScreen />}
            />
            <Route
              path='/metric/:volume_id/*'
              element={<VolumeMetricScreen />}
            />
            <Route
              path='/setting/:volume_id/'
              element={<VolumeSettingScreen />}
            />
            <Route
              path='/setting/:volume_id/*'
              element={<VolumeSettingScreen />}
            />
            <Route
              path='/create_folder/:volume_id/'
              element={<CreateFolderScreen />}
            />
            <Route
              path='/create_folder/:volume_id/*'
              element={<CreateFolderScreen />}
            />
            <Route path='/device/:device_id' element={<DeviceScreen />} />
            <Route path='/account' element={<AccountScreen />} />
            <Route path='/org' element={<OrgListScreen />} />
            <Route path='/org/:org_id' element={<OrgScreen />} />
            <Route
              path='/org/:org_id/bucket/:s3_bucket_id'
              element={<BucketEditScreen />}
            />
            <Route path='/admin/user' element={<UserListScreen />} />
            <Route path='/admin/user/:user_id' element={<UserEditScreen />} />
          </Route>
          <Route path='*' element={<NotFoundScreen />} />
        </Routes>
        <ModalDialog />
      </>
    );
  }
  return content;
}
function ProtectedLayout(props) {
  const outlet = useOutlet();
  return <ProtectedRoute>{outlet}</ProtectedRoute>;
}
function ProtectedRoute(props) {
  const is_logged_in = UserStore.useIsLoggedIn();
  const location = useLocation();
  return is_logged_in ? (
    props.children
  ) : (
    <Navigate to='/login' state={{ from: location.pathname }} />
  );
}
